// AN UNPAID SCREEN NEVER LOOKS FINISHED (17 Oct 2026).
//
// Replay 01a1497c (iPhone, Full Valet £215): a refused Apple Pay tap asked
// for the house number; iOS AutoFill filled it and within 20 ms the warning
// and the sticky bar were gone, with nothing in their place. She tapped
// "Pay by card", read "Your booking confirmation goes here." under a focused
// email box, and left believing she had booked. So while the booking is
// unpaid the pay area always says so, in one line right above the wallet
// button, and the phone bar never goes away (mobile-booking-bar).
//
// Pure, so it runs under node --test (unpaid-status.test.mjs). No em dashes.
import { fields } from '../store/constants/fields.js'

// ONE STEP AT A TIME (19 Oct 2026). Robin, on "You haven't booked yet: add
// your car size, postcode, house number and mobile, then tap Apple Pay": "can
// we finesse this UI ... its a bit offputting atm?". The line no longer opens
// on what has not happened or lists everything still to do. It names the one
// next thing, as a link that goes there, and says how many are left:
//   "Next: your car size"
//   "3 quick details, then Apple Pay. Next: your car size"
//   "All set. Tap Apple Pay to book."
//   "Welcome back. Next: your house number"
// Calm grey until a pay press with something missing turns it red (as before).

// The link for the first missing thing, in the order the page shows them
// (step-book's `missing`: wash, car size, day, then the card's postcode,
// house number, the registration (both paths, since 20 Oct 2026) and mobile;
// with the wallet sheet the registration comes before the card's fields).
const NEXT = {
  wash: 'pick a wash',
  [fields.carSize]: 'your car size',
  [fields.date]: 'choose a day',
  [fields.timeSlot]: 'pick a time',
  [fields.postcode]: 'your postcode',
  [fields.address]: 'your house number',
  [fields.carLocation]: 'your car location',
  [fields.carRegistration]: 'your registration',
  [fields.mobile]: 'your mobile'
}

// The same field as the analytics events name it (field_blocked and the rest).
const ANALYTICS = { [fields.carSize]: 'car_size', [fields.date]: 'date', [fields.timeSlot]: 'time_slot', [fields.address]: 'house_number', [fields.carLocation]: 'car_location', [fields.carRegistration]: 'registration' }
export const analyticsMissing = missing => (missing || []).map(name => ANALYTICS[name] || name)

// What to tap, for the wallet that is on screen: 'apple_pay', 'google_pay',
// 'link', or null when there is none and the card carries the payment.
const ACTIONS = { apple_pay: 'tap Apple Pay', google_pay: 'tap Google Pay', link: 'pay below' }
export const payAction = wallet => ACTIONS[wallet] || 'pay by card'
// "then Apple Pay" after the count.
const THEN = { apple_pay: 'Apple Pay', google_pay: 'Google Pay', link: 'pay below' }
const thenWord = wallet => THEN[wallet] || 'pay by card'
const capital = s => `${s.charAt(0).toUpperCase()}${s.slice(1)}`

// { reason, lead, leadShort, link, text, textShort, missingCount, nextField }
//   reason:    'missing' | 'ready' | 'returned' (back on an unpaid form; for
//              unpaid_status_shown)
//   lead:      the words before the link: "Next:", "3 quick details, then
//              Apple Pay. Next:", "Welcome back. Next:"; with nothing missing
//              the whole line, "All set. Tap Apple Pay to book." ("Now tap"
//              when nudged: a refused wallet tap whose fields are now filled)
//   leadShort: the lead without "then Apple Pay", for when the full line
//              would wrap on a phone (same as lead when there is none)
//   link:      the first missing thing, "your car size", or null
//   nextField: that field as analytics names it ('car_size'), or null
export const unpaidStatus = ({ missing = [], wallet = null, returned = false, nudged = false } = {}) => {
  const missingCount = missing.length
  const first = missing[0]
  const link = first ? NEXT[first] || `your ${first}` : null
  const nextField = first ? analyticsMissing([first])[0] : null
  let lead
  let leadShort
  if (!missingCount) {
    lead = `${returned ? 'Welcome back. ' : 'All set. '}${capital(`${nudged ? 'now ' : ''}${payAction(wallet)}`)} to book.`
    leadShort = lead
  } else if (returned) {
    lead = leadShort = 'Welcome back. Next:'
  } else if (missingCount === 1) {
    lead = leadShort = 'Next:'
  } else {
    lead = `${missingCount} quick details, then ${thenWord(wallet)}. Next:`
    leadShort = `${missingCount} quick details. Next:`
  }
  const reason = returned ? 'returned' : missingCount ? 'missing' : 'ready'
  const join = l => (link ? `${l} ${link}` : l)
  return { reason, lead, leadShort, link, text: join(lead), textShort: join(leadShort), missingCount, nextField }
}

// The phone bar's line on Book & pay: "Not booked yet", and once only the
// payment is left (barTarget 'pay') what to tap. wallet undefined: the
// screen's wallet is not known (the legacy pay buttons), "Finish payment".
export const barStatusText = ({ target, wallet } = {}) => {
  if (target !== 'pay') return 'Not booked yet'
  const action = wallet === undefined ? 'finish payment' : payAction(wallet)
  return `Not booked yet · ${action.charAt(0).toUpperCase()}${action.slice(1)}`
}

// THE WALLET PATH ASKS FOR TAPS ONLY (18 Oct 2026). Robin: "fewest boxes and
// fields is what Im thinking..". A wallet payer types nothing: the Apple Pay /
// Google Pay / Link sheet gives the address, the phone and the email
// (lib/wallet-address.js). So on the wallet path the line lists only the taps
// still missing (wash, car size, day; the time is never missing, Anytime is
// preselected) and the registration (required on both paths since 20 Oct
// 2026), never the postcode, house number or mobile, which only the card
// path asks for.
export const TYPED_FIELDS = [fields.carLocation, fields.postcode, fields.address, fields.mobile]
export const missingForPath = (missing, path) =>
  path === 'wallet' ? (missing || []).filter(name => !TYPED_FIELDS.includes(name)) : missing || []

// The wallet a platform shows (lib/checkout-mode.js WALLETS_BY_PLATFORM).
export const platformWallet = platform => ({ apple: 'apple_pay', android: 'google_pay', other: 'link' })[platform] || null
